import { Box, Paper } from '@mui/material';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import { AppContext } from 'src/contexts/AppContext';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';

type LayoutProps = {
  children?: ReactNode;
};

const widthBreakpoint = 1025;

// Hur länge (ms) det ska vara tyst från scroll-events innan vi tolkar det
// som att scrollandet har stannat och navbaren ska visas igen.
const SCROLL_STOP_DELAY_MS = 200;
// Minsta scroll-delta (px) innan vi räknar det som en avsiktlig
// upp/ner-rörelse, för att undvika flimmer från subpixel-/momentum-scroll.
const SCROLL_DIRECTION_THRESHOLD_PX = 4;

/** Syns inte, men läses av skärmläsare (samma mönster som MUI:s visuallyHidden). */
const srOnly = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

const Layout = ({ children }: LayoutProps) => {
  const { setPageHeight, navbarRef, showNavbar, setScrollNavbarVisible } = useContext(AppContext);
  const { selectedTheme, isDarkMode } = useThemePreference();
  const themeTokens = getThemeTokens(selectedTheme, isDarkMode);
  const [navbarHeight, setNavbarHeight] = useState<number>(0);
  const pageRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();

  const setNewPageHeight = () => {
    const newHeight = pageRef?.current?.clientHeight;
    if (newHeight) {
      setPageHeight(newHeight);
    }
  };

  useEffect(() => {
    setNewPageHeight();
    window.addEventListener('resize', setNewPageHeight);
    return () => {
      window.removeEventListener('resize', setNewPageHeight);
    };
  }, [pageRef]);

  useEffect(() => {
    const newHeight = navbarRef.current?.clientHeight ?? 0;
    setNavbarHeight(showNavbar ? newHeight : 0);
  }, [navbarRef.current, showNavbar]);

  // Ny sida/rutt -> nollställ scroll-driven synlighet så att baren alltid
  // startar synlig på en ny sida, oavsett scroll-läget på förra sidan
  // (huvudelementet <main> återanvänds mellan ruttbyten i denna SPA).
  useEffect(() => {
    setScrollNavbarVisible(true);
  }, [location.pathname, setScrollNavbarVisible]);

  // Visa/dölj navbaren baserat på scrollriktning inom sidan. `scroll`-events
  // bubblar INTE i DOM:en, så en lyssnare på <main> fångar bara scroll om
  // <main> själv är det element som faktiskt scrollar (overflow: auto).
  // Vissa sidor har istället ett eget nästlat element med sin egen
  // overflow: auto/scroll (t.ex. BoardSwiper på /notes, vars container har
  // exakt samma höjd som <main>s tillgängliga yta - då scrollar <main>
  // aldrig och en lyssnare där ser inga events överhuvudtaget).
  //
  // Lösningen: lyssna i CAPTURE-fasen på pageRef (den gemensamma förfadern
  // till <main> och alla nästlade scroll-containers inom sidan). Scroll-
  // events dispatchas genom capture-fasen ner via alla förfäder till målet,
  // så detta fångar scroll oavsett vilket element inuti sidan som faktiskt
  // scrollar. Vi läser scroll-position från event.target (det faktiska
  // scrollade elementet) och håller reda på lastScrollTop PER element,
  // eftersom flera oberoende scrollbara element kan finnas på samma sida.
  //
  // Detta är medvetet en EGEN flagga (scrollNavbarVisible) skild från
  // showNavbar, som andra sidor (t.ex. CreateActivityPage, EditActivityPage)
  // redan sätter till false för att helt dölja navbaren under sin livstid -
  // se AppContext.tsx.
  useEffect(() => {
    const pageEl = pageRef.current;
    if (!pageEl) return;

    const lastScrollTopByTarget = new WeakMap<EventTarget, number>();
    const stopTimer: { current: ReturnType<typeof setTimeout> | undefined } = {
      current: undefined,
    };

    const handleScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const isScrollable = target.scrollHeight > target.clientHeight;
      if (!isScrollable) {
        // Kort sida utan eget scrollutrymme - baren ska alltid vara synlig.
        setScrollNavbarVisible(true);
        return;
      }

      const scrollTop = target.scrollTop;
      const lastScrollTop = lastScrollTopByTarget.get(target) ?? scrollTop;
      const delta = scrollTop - lastScrollTop;
      lastScrollTopByTarget.set(target, scrollTop);

      if (delta > SCROLL_DIRECTION_THRESHOLD_PX) {
        setScrollNavbarVisible(false);
      } else if (delta < -SCROLL_DIRECTION_THRESHOLD_PX) {
        setScrollNavbarVisible(true);
      }

      if (stopTimer.current) clearTimeout(stopTimer.current);
      stopTimer.current = setTimeout(() => {
        setScrollNavbarVisible(true);
      }, SCROLL_STOP_DELAY_MS);
    };

    pageEl.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => {
      pageEl.removeEventListener('scroll', handleScroll, { capture: true });
      if (stopTimer.current) clearTimeout(stopTimer.current);
    };
  }, [setScrollNavbarVisible]);

  const userAgent = navigator.userAgent;
  const isMobile = !!userAgent.match(/mobile/i);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          background: (theme) => theme.palette.backdrop,
        }}
      >
        <Paper
          elevation={4}
          sx={(theme) => ({
            ...(isMobile
              ? null
              : {
                  [theme.breakpoints.up(widthBreakpoint)]: {
                    maxWidth: `${widthBreakpoint}px`,
                    maxHeight: '95vh',
                  },
                }),
            height: '100%',
            width: '100%',
            paddingBottom: `${navbarHeight}px`,
            position: 'relative',
          })}
          ref={pageRef}
        >
          <main
            ref={mainRef}
            style={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              overscrollBehavior: 'contain',
              background: themeTokens.bg,
              color: themeTokens.text,
              minHeight: '100vh',
            }}
          >
            {/* Sidans huvudrubrik. Vyerna har sina egna, synliga rubriker (som
                <h2> via temats variantMapping); utan en <h1> saknade 23 vyer
                huvudrubrik helt. Visuellt dold — appnamnet syns redan i
                webbläsarens flik och hemskärmsikonen. */}
            <Box component="h1" sx={srOnly}>
              Todej
            </Box>
            {children}
          </main>
          <Navbar />
        </Paper>
      </Box>
    </>
  );
};

export default Layout;
